import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ClipboardList, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const getNoteColor = (val) => {
  if (val === 0) return 'bg-red-200 text-red-800';
  if (val === 1) return 'bg-orange-200 text-orange-800';
  if (val === 2) return 'bg-yellow-200 text-yellow-800';
  if (val === 3) return 'bg-green-200 text-green-800';
  return 'bg-slate-50 text-slate-300';
};

const GrilleEvaluationReadOnly = ({ stagiaireId, stagiaireNom, testIdPrefix = 'grille' }) => {
  const [structure, setStructure] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});

  // Tout fermé par défaut
  useEffect(() => {
    if (structure) {
      const closed = {};
      structure.sections.forEach(s => { closed[s.id] = false; });
      setExpandedSections(closed);
    }
  }, [structure]);

  useEffect(() => {
    if (!stagiaireId) return;
    const load = async () => {
      try {
        const [structRes, dataRes] = await Promise.all([
          axios.get('/grille-evaluation/structure'),
          axios.get(`/grille-evaluation/${stagiaireId}`)
        ]);
        setStructure(structRes.data);
        setData(dataRes.data);
      } catch (e) {
      }
      setLoading(false);
    };
    load();
  }, [stagiaireId]);

  if (loading || !structure) return null;

  const hasData = data && Object.keys(data.evaluations || {}).length > 0;

  if (!hasData) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid={`${testIdPrefix}-evaluation-empty`}>
        <div className="flex items-center gap-2 mb-4">
          <ClipboardList className="h-5 w-5 text-amber-500" />
          <h3 className="text-lg font-semibold text-slate-800">
            Grille d'Evaluation {stagiaireNom ? `- ${stagiaireNom}` : ''}
          </h3>
        </div>
        <div className="text-center py-6 bg-slate-50 rounded-lg">
          <ClipboardList className="h-10 w-10 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500 text-sm">Le tuteur n'a pas encore rempli la grille d'evaluation</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid={`${testIdPrefix}-evaluation`}>
      <div className="flex items-center gap-2 mb-4">
        <ClipboardList className="h-5 w-5 text-amber-500" />
        <h3 className="text-lg font-semibold text-slate-800">
          Grille d'Evaluation {stagiaireNom ? `- ${stagiaireNom}` : ''}
        </h3>
      </div>
      
      {/* Legende */}
      <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded">0 = Non integre</span>
        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded">1 = Debut d'acquisition</span>
        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">2 = En cours</span>
        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">3 = Acquis</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left p-2 border border-slate-200 min-w-[280px]">Critere</th>
              {structure.periodes.map(p => (
                <th key={p.id} className="p-2 border border-slate-200 text-center min-w-[65px]">
                  <div className="text-xs font-bold">{p.mois}</div>
                  <div className="text-xs text-slate-500">{p.label}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {structure.sections.map(section => {
              const isExpanded = expandedSections[section.id] === true;
              return (
              <React.Fragment key={section.id}>
                <tr className="cursor-pointer" onClick={() => setExpandedSections(prev => ({...prev, [section.id]: !isExpanded}))}>
                  <td colSpan={structure.periodes.length + 1} className="p-2 bg-amber-50 border border-slate-200 font-bold text-amber-800 text-xs uppercase tracking-wide">
                    <div className="flex items-center justify-between">
                      <span>{section.titre}</span>
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                  </td>
                </tr>
                {isExpanded && section.criteres.map((critere, idx) => {
                  const key = `${section.id}_${idx}`;
                  return (
                    <tr key={key}>
                      <td className="p-2 border border-slate-200 text-xs text-slate-700">{critere}</td>
                      {structure.periodes.map(p => {
                        const val = data.evaluations?.[key]?.[p.id];
                        const label = val === 0 ? 'Non intégré' : val === 1 ? "Début d'acquisition" : val === 2 ? 'En cours' : val === 3 ? 'Acquis' : 'Non évalué';
                        const handleCellClick = () => {
                          if (val !== undefined && val !== null) {
                            toast.info(`${val} = ${label}`, { description: p.label, duration: 2000 });
                          }
                        };
                        return (
                          <td key={p.id} onClick={handleCellClick} className={`p-2 border border-slate-200 text-center text-xs font-bold cursor-pointer select-none ${getNoteColor(val)}`}
                              data-testid={`${testIdPrefix}-cell-${key}-${p.id}`}>
                            {val !== undefined && val !== null ? val : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {data.commentaire_global && (
        <div className="mt-3 p-3 bg-slate-50 rounded-lg border">
          <p className="text-xs font-medium text-slate-500 mb-1">Commentaire du tuteur :</p>
          <p className="text-sm text-slate-700">{data.commentaire_global}</p>
        </div>
      )}
    </div>
  );
};

export default GrilleEvaluationReadOnly;
